/**
 * Top Banner Component
 *
 * Purpose: Reusable top banner that appears at the top of every page with page title,
 *          breadcrumbs, tenant selector, help, notifications, and user profile
 *
 * Architecture: Material-UI AppBar with consistent layout and responsive design
 *
 * Dependencies: React, Material-UI, AuthContext, TenantSelection
 * Author: Development Team
 * Created: 2025-08-07
 */

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Button,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Help, Business, KeyboardArrowDown, Search } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import { useNavigationGuard } from '../../context/NavigationGuardContext';

// Custom notification icon component for SVG icon from public folder
// White icon inside dark circle to match Help icon style (28px to match enlarged Help icon)
const NotificationIcon: React.FC<{ sx?: object }> = ({ sx = {} }) => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: '50%',
      backgroundColor: '#222222',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...sx,
    }}
  >
    <Box
      component="img"
      src="/icons/orc_icon_notification.svg"
      alt="Notifications"
      sx={{
        width: 24,
        height: 24,
        objectFit: 'contain',
        // Bold effect using drop-shadow to thicken the stroke
        filter: 'drop-shadow(0 0 0.5px #fff) drop-shadow(0 0 0.5px #fff)',
      }}
    />
  </Box>
);

// Custom menu icon component for profile menu items (dark icons on light background)
interface MenuIconProps {
  src: string;
  alt: string;
}

const MenuIcon: React.FC<MenuIconProps> = ({ src, alt }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    sx={{
      width: 24,
      height: 24,
      objectFit: 'contain',
      // Convert white SVG to dark and add drop-shadow to make bolder
      filter: 'invert(1) drop-shadow(0 0 0.3px #000) drop-shadow(0 0 0.3px #000)',
    }}
  />
);

import TenantSelection from '../tenant-selection/TenantSelection';
import { useNavigate, useLocation } from 'react-router-dom';

// Styled components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
  zIndex: theme.zIndex.drawer + 1,
}));

const TenantSelector = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.primary,
  backgroundColor: 'transparent',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-disabled': {
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.divider}`,
    opacity: 1,
  },
}));

const BreadcrumbLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  textDecoration: 'none',
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
    '& fieldset': {
      border: 'none',
    },
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      '& fieldset': {
        border: `1px solid ${theme.palette.primary.main}`,
      },
    },
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1.5),
    fontSize: '0.875rem',
  },
}));

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

interface TopBannerProps {
  pageTitle: string;
  breadcrumbs?: BreadcrumbItem[];
  showSearch?: boolean;
}

/**
 * Top Banner Component
 *
 * Purpose: Displays page title, breadcrumbs, tenant selector, help, notifications, and user profile
 * Logic: Shows current page context and provides access to key user functions
 * Input: pageTitle - Current page title to display
 *        breadcrumbs - Optional breadcrumb navigation items
 *        showSearch - Whether to show the search bar (default: false)
 * Output: JSX element for top banner
 *
 * @param pageTitle - Current page title to display
 * @param breadcrumbs - Optional breadcrumb navigation items
 * @param showSearch - Whether to show the search bar
 * @returns JSX element for top banner
 */
const TopBanner: React.FC<TopBannerProps> = ({ pageTitle, breadcrumbs, showSearch = false }) => {
  const { state, logout, getCurrentTenant } = useAuth();
  const { user } = state;
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmNavigation } = useNavigationGuard();

  // State for menus, modals, and search
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [helpMenuAnchor, setHelpMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationMenuAnchor, setNotificationMenuAnchor] = useState<null | HTMLElement>(null);
  const [tenantModalOpen, setTenantModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const currentTenant = getCurrentTenant();
  const isMultiTenant = user?.availableTenants && user.availableTenants.length > 1;

  // Get user display name
  const getUserDisplayName = (): string => {
    if (user?.email) {
      const namePart = user.email.split('@')[0];
      // Capitalize first letter
      return namePart.charAt(0).toUpperCase() + namePart.slice(1);
    }
    return 'User';
  };

  // Debug logging removed for production

  // Event handlers
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleHelpMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHelpMenuAnchor(event.currentTarget);
  };

  const handleHelpMenuClose = () => {
    setHelpMenuAnchor(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationMenuAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationMenuAnchor(null);
  };

  const handleTenantSelectorClick = () => {
    if (!isMultiTenant) return;
    // Selecting swaps the active tenant server-side, so the open page's unsaved
    // draft has to be settled before the picker opens (ORCH-393)
    confirmNavigation(() => setTenantModalOpen(true));
  };

  const handleTenantSelect = (_tenantId: string) => {
    // Navigate to the current page to refresh the view with the new tenant
    navigate(location.pathname, { replace: true });
  };

  const handleLogout = () => {
    handleUserMenuClose();
    confirmNavigation(async () => {
      try {
        await logout();
        navigate('/login');
      } catch (error) {
        console.error('Logout failed:', error);
      }
    });
  };

  const handleProfile = () => {
    handleUserMenuClose();
    confirmNavigation(() => navigate('/profile'));
  };

  const handleSettings = () => {
    handleUserMenuClose();
    confirmNavigation(() => navigate('/settings'));
  };

  // Get tenant display name (truncated if > 20 characters)
  const getTenantDisplayName = (name: string): string => {
    return name.length > 20 ? `${name.substring(0, 20)}...` : name;
  };

  return (
    <>
      <StyledAppBar position="static">
        <Toolbar sx={{ minHeight: '64px' }}>
          {/* Left Side - Search or Breadcrumbs/Page Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 2 }}>
            {showSearch ? (
              /* Search Bar - Figma Design Style */
              <SearchField
                placeholder="Search..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            ) : (
              /* Breadcrumbs and Page Title */
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                {breadcrumbs && breadcrumbs.length > 0 && (
                  <Breadcrumbs sx={{ mb: 0.5 }} aria-label="breadcrumb">
                    {breadcrumbs.map((item, index) => (
                      <BreadcrumbLink
                        key={index}
                        href={item.href}
                        onClick={(e) => {
                          if (item.href) {
                            e.preventDefault();
                            // A page with unsaved changes gets to ask first (ORCH-393)
                            confirmNavigation(() => navigate(item.href as string));
                          }
                        }}
                        underline="hover"
                        color="inherit"
                      >
                        {item.icon}
                        {item.label}
                      </BreadcrumbLink>
                    ))}
                  </Breadcrumbs>
                )}
                {pageTitle && (
                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    {pageTitle}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Right Side Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Tenant Selector - Always show when tenant is available */}
            {currentTenant && (
              <Tooltip title={isMultiTenant ? 'Switch Organization' : currentTenant.name}>
                <TenantSelector
                  onClick={handleTenantSelectorClick}
                  disabled={!isMultiTenant}
                  startIcon={<Business />}
                  endIcon={isMultiTenant ? <KeyboardArrowDown /> : null}
                  sx={{
                    cursor: isMultiTenant ? 'pointer' : 'default',
                    '&:hover': {
                      backgroundColor: isMultiTenant ? 'action.hover' : 'transparent',
                    },
                  }}
                >
                  {getTenantDisplayName(currentTenant.name)}
                </TenantSelector>
              </Tooltip>
            )}

            {/* Help Icon */}
            <Tooltip title="Help">
              <IconButton size="large" onClick={handleHelpMenuOpen} color="inherit">
                <Help sx={{ fontSize: 28 }} />
              </IconButton>
            </Tooltip>

            {/* Notifications Icon */}
            <Tooltip title="Notifications">
              <IconButton size="large" onClick={handleNotificationMenuOpen} color="inherit">
                <Badge badgeContent={0} color="error">
                  <NotificationIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Name Display - Figma Design */}
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' },
                ml: 1,
              }}
            >
              {getUserDisplayName()}
            </Typography>

            {/* User Profile Avatar */}
            <Tooltip title="User Profile">
              <IconButton
                size="small"
                onClick={handleUserMenuOpen}
                color="inherit"
                aria-label="User Profile"
                data-testid="user-menu-button"
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  {getUserDisplayName().charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </StyledAppBar>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 200,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="textPrimary">
            {user?.email?.split('@')[0] || 'User'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {user?.email}
          </Typography>
        </Box>

        <Divider />

        <MenuItem onClick={handleProfile}>
          <ListItemIcon>
            <MenuIcon src="/icons/orc_icon_user.svg" alt="Profile" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <MenuIcon src="/icons/orc_icon_settings.svg" alt="Settings" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <MenuIcon src="/icons/orc_icon_logout.svg" alt="Logout" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Help Menu */}
      <Menu
        anchorEl={helpMenuAnchor}
        open={Boolean(helpMenuAnchor)}
        onClose={handleHelpMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 180,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleHelpMenuClose}>
          <ListItemText>Help Center</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleHelpMenuClose}>
          <ListItemText>Keyboard Shortcuts</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleHelpMenuClose}>
          <ListItemText>Contact Support</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleHelpMenuClose}>
          <ListItemText>What's New</ListItemText>
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationMenuAnchor}
        open={Boolean(notificationMenuAnchor)}
        onClose={handleNotificationMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 300,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No new notifications
          </Typography>
        </Box>
      </Menu>

      {/* Tenant Selection Modal */}
      <TenantSelection
        open={tenantModalOpen}
        onClose={() => setTenantModalOpen(false)}
        onTenantSelect={handleTenantSelect}
      />
    </>
  );
};

export default TopBanner;
